import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  Menu,
  InputAdornment,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  InsertLink,
  Image as ImageIcon,
  PhotoCamera,
  FormatColorText,
  FormatUnderlined,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../redux/store";
import { getDownloadURL, getStorage, ref, uploadString } from "firebase/storage";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { fetchCategories, upsertPost } from "../../../../../redux/blogSlice";

const EPost: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const categories = useSelector((state: RootState) => state.blog.categories);

  const editorRef = useRef<HTMLDivElement>(null);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [category, setCategory] = useState("");
  const [featuredImage, setFeaturedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [linkText, setLinkText] = useState("");
  const [textColor, setTextColor] = useState("#a78bfa");
  const [colorAnchorEl, setColorAnchorEl] = useState<null | HTMLElement>(null);
  const [colorActive, setColorActive] = useState(false);
  const [description, setDescription] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);



  const uploadBase64Image = async (base64: string, index: number): Promise<string> => {
    const storage = getStorage();
    const imageRef = ref(storage, `posts/editorImage-${Date.now()}-${index}.jpg`);
    await uploadString(imageRef, base64, "data_url");
    return await getDownloadURL(imageRef);
  };

  
  const generateSlug = (text: string) =>
    text.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  const exec = (command: string, value: string = "") => {
    const selection = window.getSelection();
    const isCollapsed = selection?.isCollapsed;
  
    if (command === "foreColor" && isCollapsed) {
      // apply color to future text
      const span = document.createElement("span");
      span.style.color = value;
      span.appendChild(document.createTextNode("\u200b")); // zero-width space
      const range = selection?.getRangeAt(0);
      if (range) {
        range.insertNode(span);
        range.setStart(span.firstChild!, 1);
        range.setEnd(span.firstChild!, 1);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    } else {
      document.execCommand(command, false, value);
    }
  
    editorRef.current?.focus();
  };
  

  const insertImage = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = document.createElement("img");
      img.src = reader.result as string;
      img.style.maxWidth = "100%";
      img.style.borderRadius = "8px";
      img.style.margin = "8px 0";
      const range = window.getSelection()?.getRangeAt(0);
      if (range) {
        range.insertNode(img);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleLinkInsert = () => {
    exec("createLink", linkText);
    setLinkText("");
    setAnchorEl(null);
  };
  const handlePublish = async () => {
    let content = editorRef.current?.innerHTML || "";
    if (!title || !content) {
      console.warn("Missing title or content");
      return;
    }
  
    setLoading(true);
    const postSlug = slug || generateSlug(title);
  
    try {
      const base64Matches = content.match(/<img[^>]+src="data:image\/[^"]+"[^>]*>/g) || [];  
      for (let i = 0; i < base64Matches.length; i++) {
        const tag = base64Matches[i];
        const base64Match = tag.match(/src="(data:image\/[^"]+)"/);
        if (!base64Match) continue;
  
        const base64 = base64Match[1];
        const url = await uploadBase64Image(base64, i);
        const newTag = tag.replace(base64, url);
        content = content.replace(tag, newTag);
      }
  
      // ✅ fixed — no rogue block, valid payload passed directly to dispatch
      await dispatch(
        upsertPost({
          title,
          slug: postSlug,
          content,
          featuredImage: featuredImage || "",
          category: category || undefined,
          description,
          published: true,
        })
      ).unwrap();
  
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
  
      setTitle("");
      setSlug("");
      editorRef.current!.innerHTML = "";
      setFeaturedImage(null);
      setCategory("");
    } catch (err) {
      console.error("Failed to publish post:", err);
    }
  
    setLoading(false);
  };
  

  return (
    <Box sx={{ p: 4, bgcolor: "#0a0014", minHeight: "100vh", color: "#fff" }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Add a Post
      </Typography>

      <TextField
        fullWidth
        label="Post Title"
        variant="outlined"
        value={title}
        onChange={(e) => {
          const val = e.target.value;
          setTitle(val);
          if (!slug || slug === generateSlug(title))
            setSlug(generateSlug(val));
        }}
        sx={{ mb: 2, input: { color: "#fff" }, label: { color: "#bbb" } }}
        InputProps={{ style: { background: "#1e1e2f" } }}
      />

      <TextField
        fullWidth
        label="Slug"
        variant="outlined"
        value={slug}
        onChange={(e) => setSlug(generateSlug(e.target.value))}
        sx={{ mb: 2, input: { color: "#fff" }, label: { color: "#bbb" } }}
        InputProps={{ style: { background: "#1e1e2f" } }}
      />
      <TextField
  fullWidth
  label="Description"
  variant="outlined"
  value={description}
  onChange={(e) => setDescription(e.target.value)}
  sx={{ mb: 2, input: { color: "#fff" }, label: { color: "#bbb" } }}
  InputProps={{ style: { background: "#1e1e2f" } }}
  multiline
  minRows={3}
/>


      <TextField
        fullWidth
        select
        SelectProps={{ native: true }}
        label="Category"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        sx={{ mb: 2, input: { color: "#fff" }, label: { color: "#bbb" } }}
        InputProps={{ style: { background: "#1e1e2f" } }}
      >
        <option value="">None</option>
        {categories.map((cat) => (
          <option key={cat.id} value={cat.slug}>
            {cat.title}
          </option>
        ))}
      </TextField>

      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <IconButton onClick={() => exec("bold")}>
          <FormatBold sx={{ color: "#fff" }} />
        </IconButton>
        <IconButton onClick={() => exec("italic")}>
          <FormatItalic sx={{ color: "#fff" }} />
        </IconButton>
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          <InsertLink sx={{ color: "#fff" }} />
        </IconButton>

        <IconButton onClick={() => exec("underline")}>
          <FormatUnderlined sx={{ color: "#fff" }} />
        </IconButton>


        <IconButton
  onClick={(e) => {
    setColorAnchorEl(colorAnchorEl ? null : e.currentTarget);
  }}
>
  <FormatColorText sx={{ color: textColor }} />
</IconButton>

{colorAnchorEl && (
  <Box sx={{ position: "absolute", zIndex: 9999 }}>
    <input
      type="color"
      value={textColor}
      onChange={(e) => {
        const color = e.target.value;
        setTextColor(color);
        exec("foreColor", color);
        setColorAnchorEl(null);
      }}
      style={{
        width: 32,
        height: 32,
        border: "none",
        background: "transparent",
        cursor: "pointer",
        marginTop: 4,
        marginLeft: 4,
      }}
      autoFocus
      onBlur={() => setColorAnchorEl(null)}
    />
  </Box>
)}

<IconButton component="label">
  <ImageIcon sx={{ color: "#fff" }} />
  <input
    type="file"
    hidden
    accept="image/*"
    multiple
    onChange={(e) => {
      const files = e.target.files;
      if (files) {
        Array.from(files).forEach((file) => insertImage(file));
      }
      e.target.value = "";
    }}
  />
</IconButton>

      </Box>

      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        style={{
          minHeight: 300,
          background: "#1e1e2f",
          color: "#fff",
          padding: 16,
          borderRadius: 8,
          outline: "none",
          marginBottom: 16,
          whiteSpace: "pre-wrap",
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <TextField
            size="small"
            label="Link URL"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLinkInsert()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button size="small" onClick={handleLinkInsert}>
                    Add
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Menu>



      <Box sx={{ my: 2 }}>
        <IconButton component="label">
          <PhotoCamera sx={{ color: "#fff" }} />
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = () => {
                  setFeaturedImage(reader.result as string);
                };
                reader.readAsDataURL(file);
              }
            }}
          />
        </IconButton>
      </Box>

      {featuredImage && (
        <Box sx={{ mb: 2 }}>
          <img
            src={featuredImage}
            alt="Featured"
            style={{ maxWidth: "100%", borderRadius: 8 }}
          />
        </Box>
      )}

      <Button
        variant="contained"
        onClick={handlePublish}
        sx={{
          mt: 2,
          background: "linear-gradient(to right, #a78bfa, #c084fc)",
          color: "#000",
          fontWeight: 600,
          px: 4,
        }}
        disabled={loading}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          "Publish"
        )}
      </Button>

      {success && (
  <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 1 }}>
    <CheckCircleOutlineIcon sx={{ color: "#4caf50", fontSize: 28 }} />
    <Typography sx={{ color: "#4caf50", fontWeight: 500 }}>
      Post published successfully!
    </Typography>
  </Box>
)}

    </Box>
  );
};

export default EPost;


const styles: { [key: string]: React.CSSProperties } = {
  page: {
    background: "linear-gradient(to bottom, #0a0014, #1e003a, #a78bfa)",
    minHeight: "100vh",
    padding: 20,
    display: "flex",
    justifyContent: "center",
  },
  editor: {
    width: "100%",
    maxWidth: 720,
    background: "#1a1a2f",
    padding: 20,
    borderRadius: 12,
    boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
    color: "#fff",
  },
  titleInput: {
    fontSize: 20,
    fontWeight: 600,
    width: "100%",
    padding: "12px 16px",
    marginBottom: 12,
    border: "1px solid #444",
    borderRadius: 8,
    background: "#111",
    color: "#fff",
  },
  textarea: {
    minHeight: 240,
    width: "100%",
    borderRadius: 8,
    padding: 16,
    border: "1px solid #333",
    background: "#111",
    color: "#fff",
    marginBottom: 16,
  },
  toolbar: {
    display: "flex",
    gap: 10,
    borderTop: "1px solid #444",
    borderBottom: "1px solid #444",
    padding: "10px 0",
    alignItems: "center",
  },
  imageInput: {
    display: "none",
  },
  actionRow: {
    marginTop: 20,
    display: "flex",
    justifyContent: "flex-end",
  },
  publishButton: {
    background: "linear-gradient(to right, #c084fc, #a78bfa)",
    fontWeight: 600,
    color: "#000",
  },
};